import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { RouteSpinner } from '@/components/ui/route-spinner'
import { useSessionMeta } from '@/state/session/context'
import { RouteErrorBoundary } from '@/components/feedback/error-boundary'

// Layouts (static — shared across all routes)
import { AppShell } from '@/components/layout/app-shell'
import { HabitpackThemeProvider } from '@/app/providers/habitpack-theme-provider'
import { AuthGuard } from '@/features/auth/components/guards/auth-guard'
import { OnboardingGuard } from '@/features/auth/components/guards/onboarding-guard'
import { PublicGuard } from '@/features/auth/components/guards/public-guard'

// PERF-F01: Lazy-loaded pages for code splitting — each becomes a separate chunk
const RootPage = lazy(() => import('@/features/public/routes/page'))

// Public
const LoginPage = lazy(() => import('@/features/auth/routes/public/login/page'))
const RegisterPage = lazy(() => import('@/features/auth/routes/public/register/page'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/routes/public/forgot-password/page'))

// Onboarding
const OnboardingModulesPage = lazy(() => import('@/features/auth/routes/onboarding/onboarding/modules/page'))
const OnboardingFirstHabitPage = lazy(() => import('@/features/auth/routes/onboarding/onboarding/first-habit/page'))
const OnboardingTutorialPage = lazy(() => import('@/features/auth/routes/onboarding/onboarding/tutorial/page'))

// App (authenticated)
const DashboardPage = lazy(() => import('@/features/dashboard/routes/dashboard/page'))
const ManageHabitsPage = lazy(() => import('@/features/manage/routes/manage/habits/page'))
const ManageGoalsPage = lazy(() => import('@/features/manage/routes/manage/goals/page'))
const ManageDiaryPage = lazy(() => import('@/features/manage/routes/manage/diary/page'))
const ManageProjectsAndTasksPage = lazy(() => import('@/features/manage/routes/manage/projects-and-tasks/page'))
const MissionsPage = lazy(() => import('@/features/missions/routes/missions/page'))
const AchievementsPage = lazy(() => import('@/features/dashboard/routes/achievements/page'))
const AnalyticsPage = lazy(() => import('@/features/analytics/routes/analytics/page'))
const FriendsPage = lazy(() => import('@/features/social/routes/friends/page'))
const InvitesPage = lazy(() => import('@/features/social/routes/invites/page'))
const SeasonResetPage = lazy(() => import('@/features/contracts/routes/season-reset/page'))
const YearResetPage = lazy(() => import('@/features/contracts/routes/year-reset/page'))

// Arena
const ArenaBossPage = lazy(() => import('@/features/arena/routes/arena/boss/page'))
const ArenaPetPage = lazy(() => import('@/features/arena/routes/arena/pet/page'))
const ArenaChallengesPage = lazy(() => import('@/features/arena/routes/arena/challenges/page'))
const ArenaChallengePage = lazy(() => import('@/features/arena/routes/arena/challenge/page'))
const ArenaChallengeModPage = lazy(() => import('@/features/arena/routes/arena/challenge/mod/page'))
const ArenaDuelsPage = lazy(() => import('@/features/arena/routes/arena/duels/page'))
const ArenaLeaguesPage = lazy(() => import('@/features/arena/routes/arena/leagues/page'))

// Emporium
const EmporiumShopPage = lazy(() => import('@/features/emporium/routes/emporium/shop/page'))
const EmporiumInventoryPage = lazy(() => import('@/features/emporium/routes/emporium/inventory/page'))
const EmporiumPassPage = lazy(() => import('@/features/emporium/routes/emporium/pass/page'))
const EmporiumProfilePage = lazy(() => import('@/features/emporium/routes/emporium/profile/page'))

// Settings
const SettingsPage = lazy(() => import('@/features/settings/routes/groups/settings/page'))
const SettingsAccountPage = lazy(() => import('@/features/settings/routes/groups/settings/account/page'))
const SettingsModulesPage = lazy(() => import('@/features/settings/routes/groups/settings/modules/page'))
const SettingsNotificationsPage = lazy(() => import('@/features/settings/routes/groups/settings/notifications/page'))
const SettingsPrivacyPage = lazy(() => import('@/features/settings/routes/groups/settings/privacy/page'))
const SettingsSubscriptionPage = lazy(() => import('@/features/settings/routes/groups/settings/subscription/page'))
const SettingsAccessibilityPage = lazy(() => import('@/features/settings/routes/groups/settings/accessibility/page'))

// Billing
const BillingCheckoutPage = lazy(() => import('@/features/billing/routes/groups/billing/checkout/page'))
const BillingCancelPage = lazy(() => import('@/features/billing/routes/groups/billing/cancel/page'))
const BillingSuccessPage = lazy(() => import('@/features/billing/routes/groups/billing/success/page'))

// Legal
const LegalPrivacyPage = lazy(() => import('@/features/legal/routes/groups/legal/privacy/page'))
const LegalTermsPage = lazy(() => import('@/features/legal/routes/groups/legal/terms/page'))

// Support
const SupportPage = lazy(() => import('@/features/support/routes/groups/support/page'))

// Join
const JoinCodePage = lazy(() => import('@/features/join/routes/join/[code]/page'))

function AuthenticatedLayout() {
  return (
    <AuthGuard>
      <AppShell>
        <Outlet />
      </AppShell>
    </AuthGuard>
  )
}

function PublicLayout() {
  return (
    <PublicGuard>
      <Outlet />
    </PublicGuard>
  )
}

function OnboardingLayout() {
  return (
    <OnboardingGuard>
      <Outlet />
    </OnboardingGuard>
  )
}


export default function AppRouter() {
  const { hydrated, isLoading } = useSessionMeta()

  if (!hydrated || isLoading) {
    return (
      <div className="min-h-dvh bg-background">
        <RouteSpinner className="min-h-dvh" label="INICIANDO PROTOCOLO..." showLabel />
      </div>
    )
  }

  return (
    <RouteErrorBoundary>
      <Suspense fallback={<div className="min-h-dvh bg-background"><RouteSpinner className="min-h-dvh" label="CARREGANDO..." showLabel /></div>}>
        <Routes>
          {/* Root page handles initial landing/redirect logic */}
          <Route path="/" element={<RootPage />} />

          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          </Route>

          {/* Onboarding Routes */}
          <Route element={<OnboardingLayout />}>
            <Route path="/onboarding/modules" element={<OnboardingModulesPage />} />
            <Route path="/onboarding/first-habit" element={<OnboardingFirstHabitPage />} />
            <Route path="/onboarding/tutorial" element={<OnboardingTutorialPage />} />
          </Route>

          {/* App Shell (Authenticated) */}
          <Route element={<AuthenticatedLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Manage Section */}
            <Route path="/manage/habits" element={<ManageHabitsPage />} />
            <Route path="/manage/goals" element={<ManageGoalsPage />} />
            <Route path="/manage/diary" element={<ManageDiaryPage />} />
            <Route path="/manage/projects-and-tasks" element={<ManageProjectsAndTasksPage />} />

            {/* Content & Social */}
            <Route path="/missions" element={<MissionsPage />} />
            <Route path="/achievements" element={<AchievementsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/friends" element={<FriendsPage />} />
            <Route path="/invites" element={<InvitesPage />} />

            {/* Arena */}
            <Route path="/arena/boss" element={<ArenaBossPage />} />
            <Route path="/arena/pet" element={<ArenaPetPage />} />
            <Route path="/arena/challenges" element={<ArenaChallengesPage />} />
            <Route path="/arena/challenge" element={<ArenaChallengePage />} />
            <Route path="/arena/challenge/mod" element={<ArenaChallengeModPage />} />
            <Route path="/arena/duels" element={<ArenaDuelsPage />} />
            <Route path="/arena/leagues" element={<ArenaLeaguesPage />} />

            {/* Emporium */}
            <Route path="/emporium/shop" element={<EmporiumShopPage />} />
            <Route path="/emporium/inventory" element={<EmporiumInventoryPage />} />
            <Route path="/emporium/pass" element={<EmporiumPassPage />} />
            <Route path="/emporium/profile" element={<EmporiumProfilePage />} />

            {/* Settings */}
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/account" element={<SettingsAccountPage />} />
            <Route path="/settings/modules" element={<SettingsModulesPage />} />
            <Route path="/settings/notifications" element={<SettingsNotificationsPage />} />
            <Route path="/settings/privacy" element={<SettingsPrivacyPage />} />
            <Route path="/settings/subscription" element={<SettingsSubscriptionPage />} />
            <Route path="/settings/accessibility" element={<SettingsAccessibilityPage />} />

            {/* Billing */}
            <Route path="/billing/checkout" element={<BillingCheckoutPage />} />
            <Route path="/billing/cancel" element={<BillingCancelPage />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />

            {/* Legal & Support */}
            <Route path="/legal/privacy" element={<LegalPrivacyPage />} />
            <Route path="/legal/terms" element={<LegalTermsPage />} />
            <Route path="/support" element={<SupportPage />} />

            {/* System Redirects */}
            <Route path="/manage" element={<Navigate to="/manage/habits" replace />} />
            <Route path="/emporium" element={<Navigate to="/emporium/shop" replace />} />
            <Route path="/season-reset" element={<SeasonResetPage />} />
            <Route path="/year-reset" element={<YearResetPage />} />
          </Route>

          {/* Specialized Routes (No Shell) */}
          <Route path="/join/:code" element={<JoinCodePage />} />

          {/* Catch-all Routing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  )
}
